import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ICostCategoryState } from "../../utils/costCategory.types";
import { ApiStatus } from "../../utils/api.types";
import {
    fetchCostCategories,
    fetchCostCategoryById,
    createCostCategory,
    updateCostCategory,
    deleteCostCategory
} from "./costCategoryThunk";

/**
 * Cost Category slice initial state
 */
const initialState: ICostCategoryState = {
    categories: [],
    selectedCategory: null,
    status: ApiStatus.IDLE,
    error: null
};

/**
 * Cost Category Slice
 */
const costCategorySlice = createSlice({
    name: 'costCategory',
    initialState,
    reducers: {
        clearSelectedCategory: (state) => {
            state.selectedCategory = null;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch all cost categories
            .addCase(fetchCostCategories.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchCostCategories.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.categories = Array.isArray(action.payload.data) ? action.payload.data : [];
                state.error = null;
            })
            .addCase(fetchCostCategories.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to fetch cost categories';
                toast.error(state.error);
            })

            // Fetch cost category by ID
            .addCase(fetchCostCategoryById.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchCostCategoryById.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.selectedCategory = action.payload.data as any;
                state.error = null;
            })
            .addCase(fetchCostCategoryById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to fetch cost category';
                toast.error(state.error);
            })

            // Create cost category
            .addCase(createCostCategory.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(createCostCategory.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                if (action.payload.data && !Array.isArray(action.payload.data)) {
                    state.categories.push(action.payload.data);
                }
                state.error = null;
                toast.success(action.payload.message || 'Cost Category created successfully');
            })
            .addCase(createCostCategory.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to create cost category';
                toast.error(state.error);
            })

            // Update cost category
            .addCase(updateCostCategory.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateCostCategory.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                if (action.payload.data && !Array.isArray(action.payload.data)) {
                    const data = action.payload.data;
                    const index = state.categories.findIndex(cat => cat._id === data._id);
                    if (index !== -1) {
                        state.categories[index] = data;
                    }
                }
                state.error = null;
                toast.success(action.payload.message || 'Cost Category updated successfully');
            })
            .addCase(updateCostCategory.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to update cost category';
                toast.error(state.error);
            })

            // Delete cost category
            .addCase(deleteCostCategory.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteCostCategory.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                if (action.payload.data && !Array.isArray(action.payload.data)) {
                    const data = action.payload.data;
                    state.categories = state.categories.filter(cat => cat._id !== data._id);
                }
                state.error = null;
                toast.success(action.payload.message || 'Cost Category deleted successfully');
            })
            .addCase(deleteCostCategory.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.error = action.error.message || 'Failed to delete cost category';
                toast.error(state.error);
            });
    }
});

export const { clearSelectedCategory, clearError } = costCategorySlice.actions;
export default costCategorySlice.reducer;
